"use client";

import {
  BookMarked,
  BookOpen,
  Compass,
  FileText,
  Home,
  Layers,
  Library,
  Search,
  Wrench,
} from "lucide-react";
import type { ReactNode } from "react";
import { BrandMark } from "@/components/brand-mark";
import { useBibStrings } from "@/components/citation-tool/tool-strings";
import { openCommandPalette } from "@/components/command-palette-event";
import { LanguageSelector } from "@/components/i18n/language-selector";
import Link from "@/components/locale-link";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { t } from "@/lib/i18n/interpolate";
import { cn } from "@/lib/utils";

/** Labels the studio chrome needs from the dictionary, passed down from the page. */
export interface StudioChromeStrings {
  search: string;
  languageLabel: string;
  home: string;
  tools: string;
  guides: string;
  styles: string;
  howToCite: string;
  glossary: string;
}

export interface ExploreLink {
  href: string;
  label: string;
  icon: ReactNode;
}

/** The site's hubs, as links the studio can offer from several places. */
export function exploreLinks(strings: StudioChromeStrings): ExploreLink[] {
  return [
    { href: "/", label: strings.home, icon: <Home className="size-4" /> },
    {
      href: "/guides",
      label: strings.guides,
      icon: <BookMarked className="size-4" />,
    },
    {
      href: "/tools",
      label: strings.tools,
      icon: <Wrench className="size-4" />,
    },
    {
      href: "/how-to-cite",
      label: strings.howToCite,
      icon: <FileText className="size-4" />,
    },
    {
      href: "/citation-styles",
      label: strings.styles,
      icon: <Layers className="size-4" />,
    },
    {
      href: "/glossary",
      label: strings.glossary,
      icon: <Library className="size-4" />,
    },
  ];
}

interface StyleGuideCardProps {
  styleSlug: string;
  styleName: string;
  className?: string;
}

/**
 * The one contextual link that changes with the project: the full style page for
 * whatever style the bibliography is written in.
 */
export function StyleGuideCard({
  styleSlug,
  styleName,
  className,
}: StyleGuideCardProps) {
  const bib = useBibStrings();
  return (
    <Link
      className={cn(
        "group flex items-start gap-3 rounded-xl border border-border/70 bg-background p-3 shadow-soft transition-colors hover:border-border hover:bg-muted/30",
        className
      )}
      href={`/citation-style/${styleSlug}`}
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
        <BookOpen className="size-4" />
      </span>
      <span className="min-w-0">
        <span className="block font-medium text-foreground text-sm">
          {t(bib.studioStyleGuide, { style: styleName })}
        </span>
        <span className="block text-muted-foreground text-xs leading-relaxed">
          {bib.studioStyleGuideHint}
        </span>
      </span>
    </Link>
  );
}

interface ExplorePanelProps {
  strings: StudioChromeStrings;
  styleSlug: string;
  styleName: string;
}

/**
 * Where the inspector would be when nothing is selected: the rest of the site,
 * framed around the project's style. Guides and tools were the way *into* the
 * studio; this is the way back out without leaving the workspace behind.
 */
export function ExplorePanel({
  strings,
  styleSlug,
  styleName,
}: ExplorePanelProps) {
  const bib = useBibStrings();
  return (
    <div className="flex h-full flex-col px-4 py-4">
      <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
        {bib.studioExplore}
      </p>
      <p className="mt-1 text-muted-foreground text-sm leading-relaxed">
        {bib.studioExploreIntro}
      </p>
      <StyleGuideCard
        className="mt-4"
        styleName={styleName}
        styleSlug={styleSlug}
      />
      <ul className="mt-4 space-y-0.5">
        {exploreLinks(strings).map((link) => (
          <li key={link.href}>
            <Link
              className="flex items-center gap-2.5 rounded-md px-2 py-2 text-muted-foreground text-sm transition-colors hover:bg-background hover:text-foreground"
              href={link.href}
            >
              {link.icon}
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
      <p className="mt-auto pt-6 text-muted-foreground text-xs leading-relaxed">
        {bib.studioInspectorEmpty}
      </p>
    </div>
  );
}

interface StudioChromeProps {
  locale: string;
  title: string;
  strings: StudioChromeStrings;
  styleSlug: string;
  styleName: string;
  /** The page share/embed menu, rendered by the server page. */
  share?: ReactNode;
}

/**
 * The studio's own top bar, replacing the site header: brand, the page title as the
 * document's h1, then search, an Explore menu (hubs plus the project's style guide),
 * language, theme, and share.
 */
export function StudioChrome({
  locale,
  title,
  strings,
  styleSlug,
  styleName,
  share,
}: StudioChromeProps) {
  const bib = useBibStrings();
  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-border/70 border-b bg-background/95 px-2 backdrop-blur-sm sm:px-4">
      <Link
        className="flex shrink-0 items-center gap-2.5 font-semibold text-foreground tracking-tight transition-opacity hover:opacity-80"
        href="/"
      >
        <BrandMark />
        <span className="max-sm:sr-only">CitationEasy</span>
      </Link>
      <span aria-hidden="true" className="hidden text-border sm:inline">
        /
      </span>
      <h1 className="min-w-0 truncate font-medium text-base max-sm:sr-only">
        {title}
      </h1>
      <div className="ms-auto flex shrink-0 items-center gap-1 sm:gap-2">
        <Button
          aria-haspopup="dialog"
          aria-keyshortcuts="Control+K Meta+K"
          aria-label={strings.search}
          className="hidden gap-2 px-2 sm:inline-flex sm:px-3"
          onClick={openCommandPalette}
          size="sm"
          type="button"
          variant="ghost"
        >
          <Search aria-hidden="true" className="size-4" />
          <span className="hidden sm:inline">{strings.search}</span>
        </Button>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              className="gap-2 px-2 sm:px-3"
              size="sm"
              type="button"
              variant="outline"
            >
              <Compass aria-hidden="true" className="size-4" />
              <span className="hidden sm:inline">{bib.studioExplore}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 p-2">
            <StyleGuideCard styleName={styleName} styleSlug={styleSlug} />
            <ul className="mt-2 space-y-0.5">
              {/* Below `sm` the top bar has no room for a search button; the palette lives here. */}
              <li className="sm:hidden">
                <button
                  aria-haspopup="dialog"
                  className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-start text-sm transition-colors hover:bg-muted"
                  onClick={openCommandPalette}
                  type="button"
                >
                  <Search className="size-4" />
                  {strings.search}
                </button>
              </li>
              {exploreLinks(strings).map((link) => (
                <li key={link.href}>
                  <Link
                    className="flex items-center gap-2.5 rounded-md px-2 py-2 text-sm transition-colors hover:bg-muted"
                    href={link.href}
                  >
                    {link.icon}
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
        <LanguageSelector
          align="end"
          compact
          currentLocale={locale}
          englishPath="/bibliography"
          label={strings.languageLabel}
          placement="bottom"
        />
        <ThemeToggle />
        {share}
      </div>
    </header>
  );
}
